import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/base/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";

const hotKeywords = ["顶流", "综艺", "红毯", "短剧", "演唱会", "纪录片"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(query);

  usePageMeta({
    title: query ? `“${query}” 的搜索结果 - 黑料视频` : "搜索 - 黑料视频",
    description: query
      ? `黑料视频搜索“${query}”的结果，为你找到相关的娱乐八卦视频资讯。`
      : "在黑料视频中搜索明星动态、网络热议与热门视频解析。",
    keywords: "黑料视频,视频搜索,娱乐八卦,明星视频",
  });

  const categoryNameMap: Record<string, string> = categories.reduce(
    (acc, cat) => ({ ...acc, [cat.id]: cat.name }),
    {},
  );

  const results = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return videos.filter((video) => {
      const catName = categoryNameMap[video.categoryId] ?? "";
      const haystack = [
        video.title,
        video.excerpt,
        video.description,
        video.source,
        catName,
        video.tags.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, categoryNameMap]);

  const recommended = useMemo(
    () => [...videos].sort((a, b) => b.views - a.views).slice(0, 4),
    [],
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const runKeyword = (keyword: string) => {
    setInput(keyword);
    setSearchParams({ q: keyword });
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            {query ? `“${query}” 的搜索结果` : "全站搜索"}
          </h1>
          <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl items-center gap-2">
            <div className="flex flex-1 items-center rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入明星姓名、事件关键词或栏目名"
                className="w-full bg-transparent px-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                aria-label="搜索关键词"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {hotKeywords.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => runKeyword(kw)}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6">
          {query ? (
            results.length > 0 ? (
              <>
                <p className="mb-4 text-sm text-foreground-500">
                  共找到 <span className="font-medium text-primary-600">{results.length}</span> 条与“
                  {query}”相关的视频
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {results.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      categoryName={categoryNameMap[video.categoryId]}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <h2 className="mt-5 font-heading text-lg font-black text-foreground-950">
                  没有找到与“{query}”相关的视频
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词试试，或先看看大家都在看的热门内容。
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/"
                    className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                  <Link
                    to="/category/hot-review"
                    className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    查看热门视频
                  </Link>
                </div>
              </div>
            )
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-500">
                <i className="ri-search-line text-3xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-black text-foreground-950">
                输入关键词，开始搜索
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                支持搜索明星姓名、事件关键词与栏目名称。
              </p>
            </div>
          )}

          {/* Recommended hot videos */}
          <div className="mt-12">
            <h2 className="mb-5 font-heading text-lg font-black text-foreground-950 md:text-xl">
              热门视频推荐
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {recommended.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  categoryName={categoryNameMap[video.categoryId]}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}